/**
 * Etapas do fluxo digital (solicitação informada pelo anfitrião).
 * Usado em SolicitacaoVisitaView.
 */
/** Texto único dos botões de ação (recepção marca cada etapa concluída). */
export const TEXTO_BOTAO_FLUXO_DIGITAL_ETAPA = "Registrar etapa concluída";

/**
 * Dispensa o fluxo digital da recepção (e-mail, confirmação, sala e logística)
 * quando a visita NÃO for de cliente nem de parceiro. Cliente ou parceiro
 * passam a exigir o fluxo digital.
 * @param {Record<string, unknown>|null|undefined} item
 */
export function isentaFluxoDigitalRecepcao(item) {
  if (!item) return false;
  const cliente = !!(item.tipoCliente ?? item.tipo_cliente);
  const parceiro = !!(item.tipoParceiros ?? item.tipo_parceiros);
  return !(cliente || parceiro);
}

export const ETAPAS_FLUXO_DIGITAL = [
  {
    num: 1,
    titulo: "Envio de e-mail ao visitante",
    descricao: "Realização do envio do convite digital ao visitante.",
    patch: "conviteEnviadoEm",
    fallback: null,
    salvando: "convite",
    curta: "E-mail",
    toast: "E-mail ao visitante registrado.",
  },
  {
    num: 2,
    titulo: "Confirmação de presença do visitante",
    descricao: "Recebimento da confirmação de participação por parte do visitante.",
    patch: "confirmacaoPresencaVisitanteEm",
    fallback: "informacoesPreenchidasEm",
    salvando: "presenca",
    curta: "Confirmação",
    toast: "Confirmação de presença registrada.",
  },
  {
    num: 3,
    titulo: "Agendamento de sala",
    descricao: "Efetivação da reserva da sala para a realização da visita.",
    patch: "agendamentoSalaEfetuadoEm",
    fallback: "documentoConfidencialidadeRecebidoEm",
    salvando: "sala",
    curta: "Sala",
    toast: "Agendamento de sala registrado.",
  },
  {
    num: 4,
    titulo: "Encaminhamento das necessidades logísticas",
    descricao: "Envio das demandas logísticas ao responsável designado.",
    patch: "necessidadesLogisticasEncaminhadasEm",
    fallback: null,
    salvando: "logistica",
    curta: "Logística",
    toast: "Encaminhamento logístico registrado.",
  },
];

/** Data ISO da etapa (campo novo ou legado `fallback`). */
export function dataEtapaFluxoDigitalItem(item, etapa) {
  if (!item || !etapa) return null;
  const v = item[etapa.patch];
  if (v) return v;
  if (etapa.fallback) return item[etapa.fallback] ?? null;
  return null;
}

/** Paridade com `SolicitacaoVisita.conviteCompleto` (objeto plano ou instância). */
export function conviteFluxoDigitalConcluido(item) {
  if (!item) return false;
  if (isentaFluxoDigitalRecepcao(item)) return true;
  if (typeof item.conviteCompleto === "boolean") return item.conviteCompleto;
  const e1 = !!item.conviteEnviadoEm;
  const e2 = !!(item.confirmacaoPresencaVisitanteEm ?? item.informacoesPreenchidasEm);
  const e3 = !!(item.agendamentoSalaEfetuadoEm ?? item.documentoConfidencialidadeRecebidoEm);
  const e4 = !!item.necessidadesLogisticasEncaminhadasEm;
  if (e1 && e2 && e3 && e4) return true;
  if (e1 && !!item.informacoesPreenchidasEm && !!item.documentoConfidencialidadeRecebidoEm && !e4) return true;
  return false;
}

/** Linhas da lista em que o fluxo digital faz sentido (anfitrião ou já há marcos registrados). */
export function exibeFluxoDigitalNaLista(item) {
  if (!item || item.cancelada) return false;
  if (isentaFluxoDigitalRecepcao(item)) return false;
  if (item.origem === "anfitriao") return true;
  return ETAPAS_FLUXO_DIGITAL.some((e) => !!dataEtapaFluxoDigitalItem(item, e));
}

/**
 * Rótulo curto do andamento atual do fluxo digital (lista de solicitações).
 * @returns {string|null} `null` quando não há fluxo a exibir.
 */
export function rotuloStatusFluxoDigitalLista(item) {
  if (!exibeFluxoDigitalNaLista(item)) return null;
  if (conviteFluxoDigitalConcluido(item)) return "Fluxo concluído";
  const pendente = ETAPAS_FLUXO_DIGITAL.find((e) => !dataEtapaFluxoDigitalItem(item, e));
  return pendente ? `Pendente: ${pendente.curta}` : "Fluxo concluído";
}

/** Nome curto da etapa pendente (lista/UI em duas linhas); `null` se não há pendência. */
export function curtaEtapaFluxoDigitalPendente(item) {
  if (!exibeFluxoDigitalNaLista(item) || conviteFluxoDigitalConcluido(item)) return null;
  const pendente = ETAPAS_FLUXO_DIGITAL.find((e) => !dataEtapaFluxoDigitalItem(item, e));
  return pendente?.curta ?? null;
}

/**
 * Status mínimo sugerido após marcar uma etapa do fluxo (1–4 em status_agendamento).
 * Mantém avanço alinhado ao andamento na lista da recepção.
 */
export function statusAgendamentoMinimoAposEtapa(patch) {
  switch (patch) {
    case "conviteEnviadoEm":
      return 2;
    case "confirmacaoPresencaVisitanteEm":
      return 3;
    case "agendamentoSalaEfetuadoEm":
      return 3;
    case "necessidadesLogisticasEncaminhadasEm":
      return 4;
    default:
      return null;
  }
}
